/**
 * useCalendarReservations Hook
 * 
 * Purpose:
 * Custom hook for fetching calendar reservations data with race condition mitigation,
 * cancellation support, and comprehensive error handling. Handles month-scoped reservation
 * fetching with widened date ranges to capture reservations that span month boundaries.
 * 
 * High-level Workflow:
 * 1. Guard checks: Validates prerequisites (listingId, propertiesLoading, selectedProperty)
 *    before attempting any fetch. If prerequisites not met, sets loading state and clears data.
 * 2. Race condition mitigation: Uses fetchKey (listingId-year-month) and refs (inFlightFetchRef,
 *    lastFetchedKeyRef) to prevent duplicate fetches. Synchronous guards block duplicate
 *    fetches before async work begins.
 * 3. Date range calculation: Calculates month bounds, then widens fetch range by 30 days
 *    before month start and 1 day after month end to capture overlapping reservations.
 * 4. API fetch: Calls getReservations with listingId and date range. Uses AbortController
 *    for cancellation on property switch or unmount.
 * 5. Response processing: Extracts items and metadata from response (handles wrapped/flat formats).
 *    Only updates state if fetch is still current (prevents race conditions).
 * 6. Error handling: Special handling for 401/403 (treats as "auth not ready", allows retry).
 *    Other errors show user-facing error message.
 * 7. Cleanup: Aborts in-flight requests on property switch or unmount.
 * 
 * Assumptions & Invariants:
 * - Fetch gating: All fetches gated by listingId exists, propertiesLoading is false, and
 *   selectedProperty object exists. This prevents race conditions where listingId becomes
 *   available before properties fetch completes or auth is ready.
 * - Race condition mitigation: Uses inFlightFetchRef and lastFetchedKeyRef with month-key
 *   format "${listingId}-${year}-${month}" to prevent duplicate fetches. Synchronous guards
 *   check for in-flight fetches before starting async work.
 * - Fetch key format: "${listingId}-${year}-${month}" where month is 1-12 (not 0-11).
 *   This ensures unique keys per property-month combination.
 * - Date range widening: Fetches from 30 days before month start to 1 day after month end
 *   to capture reservations that span month boundaries. This ensures calendar displays all
 *   overlapping reservations correctly.
 * - AbortController: Used for request cancellation. Note: getReservations doesn't support
 *   AbortController yet, but we check abort signal before updating state to prevent race
 *   conditions.
 * - Error classification: 401/403 errors are treated as "auth not ready" (not permanent
 *   failures). Clears error state, keeps loading true, allows retry when auth becomes ready.
 *   Other errors show user-facing error message.
 * - State updates: Only updates state if inFlightFetchRef.current === fetchKey (prevents
 *   race conditions from concurrent fetches).
 * - Performance tracking: Uses bookingsFetchCountRef to track fetch count, performance
 *   marks for timing, and PERF_DEBUG flag for detailed logging.
 * 
 * Dependencies:
 * - Internal:
 *   - services/ownerApi.js: getReservations API function
 *   - utils/calendarCalculations.js: getMonthBounds (for month boundary calculation)
 * - External:
 *   - react: useState, useEffect, useRef, useMemo
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 2 refactoring)
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { getReservations } from '../services/ownerApi.js';
import { getMonthBounds } from '../utils/calendarCalculations.js';

// Performance debug flag (from env or URL param)
const PERF_DEBUG = typeof window !== 'undefined' && (
  import.meta.env.VITE_OWNER_PERF_DEBUG === '1' ||
  new URLSearchParams(window.location.search).get('perf_debug') === '1'
);

/**
 * Hook for fetching calendar reservations with race condition mitigation
 * 
 * @param {number|null} listingId - Property listing ID (primitive, stable)
 * @param {Date} calendarMonth - Selected month (triggers fetch on change)
 * @param {boolean} propertiesLoading - Whether properties are still loading
 * @param {Object|null} selectedProperty - Selected property object (for guard check)
 * @param {Object} monthStart - Start of month (Date object)
 * @param {Object} monthEnd - End of month (Date object)
 * @returns {Object} { reservations, reservationsMetadata, reservationsLoading, reservationsError, bookingsFetchCount }
 */
export function useCalendarReservations({
  listingId,
  calendarMonth,
  propertiesLoading,
  selectedProperty,
  monthStart,
  monthEnd,
}) {
  // State for reservations data
  const [reservations, setReservations] = useState([]);
  const [reservationsMetadata, setReservationsMetadata] = useState(null);
  const [reservationsLoading, setReservationsLoading] = useState(true);
  const [reservationsError, setReservationsError] = useState(null);

  // Performance tracking refs
  const bookingsFetchCountRef = useRef(0);

  // STRICT SINGLE-FETCH ENFORCEMENT: Track in-flight fetch by month-key
  // Format: `${listingId}-${year}-${month}` (e.g., "12345-2025-1" for Jan 2025)
  const inFlightFetchRef = useRef(null);
  const lastFetchedKeyRef = useRef(null);

  // Debug mode (only when ?debug=1)
  const debug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

  // Fetch live reservations - use wide range for calendar display, month bounds for KPIs
  useEffect(() => {
    // SYNCHRONOUS GUARD: Check for duplicate fetch BEFORE any async work
    // This prevents multiple useEffect calls from all starting fetches
    // CRITICAL FIX: Also check propertiesLoading AND selectedProperty to prevent race condition where
    // listingId becomes available before properties fetch completes (or fails), or auth isn't ready
    // Must have: properties loaded, selectedProperty present, and listingId available
    if (!listingId || propertiesLoading || !selectedProperty) {
      // Keep loading state true if we're waiting for prerequisites (don't show error prematurely)
      if (propertiesLoading || !selectedProperty) {
        setReservationsLoading(true);
      } else {
        setReservationsLoading(false);
      }
      setReservations([]);
      setReservationsMetadata(null);
      setReservationsError(null); // Clear error when prerequisites not met
      lastFetchedKeyRef.current = null;
      if (PERF_DEBUG) {
        console.log('[OWNER_PERF] useEffect triggered (prerequisites not met)', {
          listingId,
          propertiesLoading,
          hasSelectedProperty: !!selectedProperty,
          calendarMonth: calendarMonth.toISOString().split('T')[0],
          timestamp: new Date().toISOString(),
        });
      }
      return;
    }

    // Generate strict month-key: listingId-year-month (e.g., "12345-2025-1")
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth() + 1; // 1-12, not 0-11
    const fetchKey = `${listingId}-${year}-${month}`;
    
    // STRICT GUARD: Block if this exact month-key is already in-flight
    if (inFlightFetchRef.current === fetchKey) {
      if (PERF_DEBUG) {
        const stackTrace = new Error().stack?.split('\n').slice(1, 6).join('\n') || 'N/A';
        console.warn('[OWNER_PERF] FETCH BLOCKED - Duplicate month-key detected', {
          fetchKey,
          listingId,
          year,
          month,
          calendarMonth: calendarMonth.toISOString().split('T')[0],
          currentInFlight: inFlightFetchRef.current,
          lastFetched: lastFetchedKeyRef.current,
          timestamp: new Date().toISOString(),
          stackTrace,
        });
      }
      return; // BLOCK duplicate fetch synchronously
    }

    // STRICT GUARD: Block if this exact month-key was just fetched (prevents rapid re-renders)
    if (lastFetchedKeyRef.current === fetchKey) {
      if (PERF_DEBUG) {
        const stackTrace = new Error().stack?.split('\n').slice(1, 6).join('\n') || 'N/A';
        console.warn('[OWNER_PERF] FETCH BLOCKED - Month-key already fetched', {
          fetchKey,
          listingId,
          year,
          month,
          calendarMonth: calendarMonth.toISOString().split('T')[0],
          lastFetched: lastFetchedKeyRef.current,
          timestamp: new Date().toISOString(),
          stackTrace,
        });
      }
      return; // BLOCK duplicate fetch synchronously
    }

    // Mark this fetch as in-flight IMMEDIATELY (synchronously)
    inFlightFetchRef.current = fetchKey;
    
    if (PERF_DEBUG) {
      const stackTrace = new Error().stack?.split('\n').slice(1, 6).join('\n') || 'N/A';
      console.log('[OWNER_PERF] useEffect triggered (fetch will proceed)', {
        fetchKey,
        listingId,
        year,
        month,
        calendarMonth: calendarMonth.toISOString().split('T')[0],
        monthStart: monthStart.toISOString().split('T')[0],
        monthEnd: monthEnd.toISOString().split('T')[0],
        currentInFlight: inFlightFetchRef.current,
        lastFetched: lastFetchedKeyRef.current,
        timestamp: new Date().toISOString(),
        stackTrace,
      });
    }
    
    // CRITICAL FIX: Create AbortController for request cancellation on property switch
    const abortController = new AbortController();
    
    const fetchReservations = async () => {
      // Double-check prerequisites before starting fetch (defense in depth)
      // CRITICAL: Ensure properties are loaded AND selectedProperty is present
      if (propertiesLoading || !selectedProperty) {
        if (PERF_DEBUG) {
          console.warn('[OWNER_PERF] Fetch BLOCKED - prerequisites not met', {
            fetchKey,
            listingId,
            propertiesLoading,
            hasSelectedProperty: !!selectedProperty,
          });
        }
        // Clear in-flight flag to allow retry when prerequisites become available
        if (inFlightFetchRef.current === fetchKey) {
          inFlightFetchRef.current = null;
        }
        return;
      }

      // Use month-scoped date range for API fetch (always send explicit dates)
      // This ensures bounded payloads and allows navigation to any historical month
      // Start: first day of selected month, End: first day of next month (exclusive)
      const monthStartDate = new Date(monthStart);
      monthStartDate.setHours(0, 0, 0, 0);
      const monthEndDate = new Date(monthEnd);
      monthEndDate.setHours(23, 59, 59, 999);
      
      // Widen fetch range to include reservations that start before the month but overlap it
      // Fetch from 30 days before month start to 1 day after month end
      // This ensures we get all reservations that overlap the month, even if check-in is in previous month
      const fetchStartDate = new Date(monthStartDate);
      fetchStartDate.setDate(fetchStartDate.getDate() - 30); // 30 days before month start
      const fetchEndDate = new Date(monthEndDate);
      fetchEndDate.setDate(fetchEndDate.getDate() + 1); // 1 day after month end
      
      // Convert to YYYY-MM-DD format for API
      const startDate = fetchStartDate.toISOString().split('T')[0];
      const endDate = fetchEndDate.toISOString().split('T')[0];
      
      // Validate date range
      if (isNaN(monthStartDate.getTime()) || isNaN(monthEndDate.getTime()) || monthStartDate >= monthEndDate) {
        if (debug) {
          console.warn('[CalendarPage] Invalid month bounds:', { monthStart, monthEnd });
        }
        setReservationsError('Invalid month bounds');
        setReservations([]);
        setReservationsMetadata(null);
        setReservationsLoading(false);
        lastFetchedKeyRef.current = null; // Clear last fetched on validation error
        inFlightFetchRef.current = null; // Clear in-flight flag
        if (PERF_DEBUG) {
          console.warn('[OWNER_PERF] Fetch BLOCKED - Invalid month bounds', {
            fetchKey,
            monthStart,
            monthEnd,
          });
        }
        return;
      }

      setReservationsLoading(true);
      setReservationsError(null);

      // Performance tracking
      bookingsFetchCountRef.current += 1;
      const fetchId = bookingsFetchCountRef.current;
      const fetchStartTime = typeof window !== 'undefined' ? performance.now() : Date.now();
      
      if (typeof window !== 'undefined' && window.performance) {
        performance.mark(`bookings_fetch_start_${fetchId}`);
      }
      
      if (PERF_DEBUG) {
        const stackTrace = new Error().stack?.split('\n').slice(1, 6).join('\n') || 'N/A';
        console.log('[OWNER_PERF] Bookings fetch STARTED', {
          fetchId,
          fetchCount: bookingsFetchCountRef.current,
          fetchKey,
          listingId,
          year,
          month,
          calendarMonth: calendarMonth.toISOString().split('T')[0],
          startDate,
          endDate,
          monthStart: monthStartDate.toISOString().split('T')[0],
          monthEnd: monthEndDate.toISOString().split('T')[0],
          currentInFlight: inFlightFetchRef.current,
          timestamp: new Date().toISOString(),
          stackTrace,
        });
      }

      try {
        if (debug) {
          console.log('[CalendarPage] DEBUG reservations fetch REQUEST', {
            fetchKey,
            requestedRange: { startDate, endDate },
            month: `${year}-${String(month).padStart(2, '0')}`,
            listingId,
            timestamp: new Date().toISOString(),
          });
        }
        
        // NOTE: getReservations doesn't support AbortController yet, but we check abort signal
        // before updating state to prevent race conditions
        const response = await getReservations({
          listingId: listingId,
          startDate: startDate,
          endDate: endDate,
          view: 'calendar',
        });
        
        // Check if request was aborted before processing response
        if (abortController.signal.aborted) {
          if (PERF_DEBUG) {
            console.log('[OWNER_PERF] Fetch result DISCARDED (aborted)', {
              fetchKey,
              listingId,
            });
          }
          return;
        }

        // Performance tracking
        const fetchEndTime = typeof window !== 'undefined' ? performance.now() : Date.now();
        const fetchDuration = fetchEndTime - fetchStartTime;
        
        if (typeof window !== 'undefined' && window.performance) {
          performance.mark(`bookings_fetch_end_${fetchId}`);
          performance.measure(`bookings_fetch_ms_${fetchId}`, `bookings_fetch_start_${fetchId}`, `bookings_fetch_end_${fetchId}`);
        }

        // Extract items from response
        const items = response.items || response.data?.items || [];
        const responseMetadata = response.metadata || response.data?.metadata || null;
        
        if (debug) {
          let minCheckin = null;
          let maxCheckout = null;
          for (const it of items) {
            const ci = it.checkin_date || it.check_in_date;
            const co = it.checkout_date || it.check_out_date;
            if (ci && (!minCheckin || ci < minCheckin)) minCheckin = ci;
            if (co && (!maxCheckout || co > maxCheckout)) maxCheckout = co;
          }
          console.log('[CalendarPage] DEBUG reservations fetch RESPONSE', {
            fetchKey,
            requestedRange: { startDate, endDate },
            responseCoverage: { minCheckin, maxCheckout, count: items.length },
            listingId,
            timestamp: new Date().toISOString(),
          });
        }
        
        // API DEBUG: Log response details (gated by ?api_debug=1)
        const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const apiDebug = urlParams?.get('api_debug') === '1';
        
        if (apiDebug) {
          console.log('[CalendarPage] API DEBUG - Reservations response:', {
            url: `/api/v1/owner/reservations?listing_id=${listingId}&start_date=${startDate}&end_date=${endDate}`,
            status: 200,
            outcome: response.outcome,
            itemsCount: items.length,
            hasMetadata: !!responseMetadata,
            firstItem: items[0] ? {
              reservation_id: items[0].reservation_id,
              checkin_date: items[0].checkin_date,
              checkout_date: items[0].checkout_date,
              status: items[0].status,
              guest_name: items[0].guest_name,
            } : null,
            allItems: items.slice(0, 5).map(item => ({
              reservation_id: item.reservation_id,
              checkin_date: item.checkin_date,
              checkout_date: item.checkout_date,
            })),
          });
        }
        
        // TEMP DEBUG: Log raw API response count
        if (PERF_DEBUG) {
          console.log('[CalendarPage] TEMP DEBUG - API response received:', {
            rawItemsCount: items.length,
            firstBookingSample: items[0] ? {
              reservation_id: items[0].reservation_id,
              checkin_date: items[0].checkin_date,
              checkout_date: items[0].checkout_date,
              status: items[0].status,
            } : null,
          });
        }
        
        if (PERF_DEBUG) {
          const measure = typeof window !== 'undefined' ? performance.getEntriesByName(`bookings_fetch_ms_${fetchId}`)[0] : null;
          console.log('[OWNER_PERF] Bookings fetch COMPLETED', {
            fetchId,
            fetchCount: bookingsFetchCountRef.current,
            fetchKey,
            calendarMonth: calendarMonth.toISOString().split('T')[0],
            listingId,
            startDate,
            endDate,
            itemCount: items.length,
            durationMs: measure?.duration?.toFixed(2) || fetchDuration.toFixed(2),
            timestamp: new Date().toISOString(),
          });
        }
        
        // Only update state if this fetch is still the current in-flight fetch
        // (prevents race conditions from concurrent fetches)
        if (inFlightFetchRef.current === fetchKey) {
          setReservations(items);
          setReservationsMetadata(responseMetadata);
          lastFetchedKeyRef.current = fetchKey; // Mark as successfully fetched
          inFlightFetchRef.current = null; // Clear in-flight flag on success
          if (PERF_DEBUG) {
            console.log('[OWNER_PERF] Fetch result APPLIED', {
              fetchKey,
              itemCount: items.length,
            });
          }
        } else if (PERF_DEBUG) {
          console.warn('[OWNER_PERF] Fetch result DISCARDED (superseded)', {
            fetchKey,
            currentInFlight: inFlightFetchRef.current,
            itemCount: items.length,
          });
        }
      } catch (err) {
        const fetchEndTime = typeof window !== 'undefined' ? performance.now() : Date.now();
        const fetchDuration = fetchEndTime - fetchStartTime;
        
        if (PERF_DEBUG) {
          console.error('[OWNER_PERF] Bookings fetch FAILED', {
            fetchId,
            fetchCount: bookingsFetchCountRef.current,
            fetchKey,
            calendarMonth: calendarMonth.toISOString().split('T')[0],
            listingId,
            startDate,
            endDate,
            durationMs: fetchDuration.toFixed(2),
            error: err.message || String(err),
            timestamp: new Date().toISOString(),
          });
        }
        
        // Only update error state if this fetch is still the current in-flight fetch
        if (inFlightFetchRef.current === fetchKey) {
          // CRITICAL FIX: Handle 401/403 as auth not ready yet - don't show error, allow retry
          const isAuthError = err.status === 401 || err.status === 403 || 
                             err.errorCode === 'AUTH_INVALID_CREDENTIALS' ||
                             err.message?.includes('authentication') ||
                             err.message?.includes('unauthorized');
          
          if (isAuthError) {
            // Auth not ready yet - clear error state and allow retry when auth becomes ready
            console.warn('[CalendarPage] Auth not ready yet, will retry when prerequisites met:', err.message || String(err));
            setReservationsError(null); // Don't show error for auth-not-ready
            setReservations([]);
            setReservationsMetadata(null);
            lastFetchedKeyRef.current = null; // Clear last fetched on auth error (allow retry)
            inFlightFetchRef.current = null; // Clear in-flight flag on auth error
            // Keep loading state true to show "Loading property..." until auth is ready
            setReservationsLoading(true);
            if (PERF_DEBUG) {
              console.warn('[OWNER_PERF] Fetch failed due to auth not ready, will retry', {
                fetchKey,
                error: err.message || String(err),
                status: err.status,
              });
            }
          } else {
            // Real error - show error state
            console.error('[CalendarPage] Error fetching reservations:', err);
            setReservationsError(err.message || 'Failed to fetch reservations');
            setReservations([]);
            setReservationsMetadata(null);
            lastFetchedKeyRef.current = null; // Clear last fetched on error (allow retry)
            inFlightFetchRef.current = null; // Clear in-flight flag on error
            if (PERF_DEBUG) {
              console.error('[OWNER_PERF] Fetch error APPLIED', {
                fetchKey,
                error: err.message || String(err),
              });
            }
          }
        } else if (PERF_DEBUG) {
          console.warn('[OWNER_PERF] Fetch error DISCARDED (superseded)', {
            fetchKey,
            currentInFlight: inFlightFetchRef.current,
            error: err.message || String(err),
          });
        }
      } finally {
        // Only clear loading state if this fetch is still the current in-flight fetch
        if (inFlightFetchRef.current === fetchKey || inFlightFetchRef.current === null) {
          setReservationsLoading(false);
        }
      }
    };

    fetchReservations();
    
    // CRITICAL FIX: Cleanup function to cancel in-flight requests on property switch
    return () => {
      abortController.abort();
      // Clear in-flight flag if this is the current fetch
      if (inFlightFetchRef.current === fetchKey) {
        inFlightFetchRef.current = null;
      }
    };
    
    // Dependencies: Use stable listingId (primitive) instead of selectedProperty object
    // - listingId: Stable primitive value (extracted via useMemo)
    // - calendarMonth: Direct dependency (triggers fetch on month change)
    // - propertiesLoading: CRITICAL FIX - prevent fetch until properties are loaded
    // - selectedProperty: CRITICAL FIX - ensure property is actually available (not just listingId)
    //   This ensures retry when selectedProperty becomes available after auth is ready
    // - debug: Removed (only used for logging, not fetch logic)
    // - monthStart/monthEnd: Derived from calendarMonth via useMemo, not needed here
  }, [listingId, calendarMonth, propertiesLoading, selectedProperty, monthStart, monthEnd]);

  return {
    reservations,
    reservationsMetadata,
    reservationsLoading,
    reservationsError,
    bookingsFetchCount: bookingsFetchCountRef.current,
  };
}
